"use client";

import { useState } from "react";
import { AnimatePresence, m } from "motion/react";
import { Dialog, DialogDescription, DialogHeading } from "@ariakit/react";

import { Button } from "~/common/components/button";
import { useWorkspace } from "~/workspaces/hooks/use-workspace";
import { DataSourceList } from "~/data-sources/components/data-source-list";
import { useDataSources } from "~/data-sources/queries/get-data-sources-query";
import { setIsDataSourceOnboarded } from "~/data-sources/utils/data-source-onboarding";
import { getRootDialogPortal } from "~/common/components/dialog/dialog-root-provider.config";
import { useWorkspaceRedirect } from "~/common/hooks/dom/useWorkspaceRedirect";
import { useGetWorkspacePages } from "~/workspaces/queries/get-workspace-pages-query";
import { getInitialWorkspacePage } from "~/workspaces/utils/get-initial-workspace-page";
import { useAppTheme } from "~/common/hooks/dom/use-app-theme";
import { dataSourcesDialogStyle } from "./data-sources-dialog.style";

export function DataSourcesDialog() {
  const { selectedWorkspace } = useWorkspace();
  const { data: sources } = useDataSources({
    workspaceId: selectedWorkspace.id,
  });

  const initialPage = useGetWorkspacePages(
    { workspaceId: selectedWorkspace.id },
    (pages) => getInitialWorkspacePage(pages),
  );

  const [shouldRedirect, setShouldRedirect] = useState(false);

  useWorkspaceRedirect(
    shouldRedirect ? selectedWorkspace.slug : undefined,
    shouldRedirect ? initialPage.data : undefined,
  );

  const connectedCount = sources?.filter((s) => s.isConnected).length || 0;
  const totalCount = sources?.length || 0;
  const hasConnectedSources = connectedCount > 0;

  function handleContinue() {
    setIsDataSourceOnboarded(selectedWorkspace.id);
    setShouldRedirect(true);
  }

  const appTheme = useAppTheme();

  return (
    <AnimatePresence>
      <Dialog
        open
        modal
        alwaysVisible
        autoFocusOnShow={false}
        key="data-sources-dialog"
        portalElement={getRootDialogPortal}
        className={dataSourcesDialogStyle.dialog()}
      >
        <m.div
          exit={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 30 }}
          className={dataSourcesDialogStyle.container({
            theme: appTheme as "light",
          })}
        >
          <DialogHeading className={dataSourcesDialogStyle.heading()}>
            Connect your apps
            {selectedWorkspace ? ` for ${selectedWorkspace.title}` : ""}
          </DialogHeading>
          <DialogDescription className={dataSourcesDialogStyle.description()}>
            To get started log in to your accounts so we can provide you with
            the most accurate and up to date project details.
          </DialogDescription>

          <div className={dataSourcesDialogStyle.list()}>
            <DataSourceList sources={sources || []} />
          </div>

          <Button
            type="submit"
            aria-busy={false}
            background="on-white"
            className={dataSourcesDialogStyle.continueButton()}
            onClick={handleContinue}
            disabled={!hasConnectedSources}
          >
            Continue ({connectedCount} of {totalCount})
          </Button>

          <div className={dataSourcesDialogStyle.bottomText()}>
            We’ll save your credentials so you don’t have to log in every time.
            You can manage your services anytime in your profile settings.
          </div>
        </m.div>
      </Dialog>
    </AnimatePresence>
  );
}
